import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

export default function MemoryGame() {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moves, setMoves] = useState(0);
  const [pairCount, setPairCount] = useState(6); // default

  useEffect(() => {
    loadCards();
  }, [pairCount]);

  const loadCards = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('characters')
      .select('id, name, image')
      .not('image', 'is', null);

    if (error) {
      console.error('Error loading characters:', error);
      return;
    }

    const shuffled = data.sort(() => 0.5 - Math.random()).slice(0, pairCount);
    const duplicated = [...shuffled, ...shuffled]
      .map((card) => ({
        ...card,
        uuid: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
      }))
      .sort(() => 0.5 - Math.random());

    setCards(duplicated);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setLoading(false);
  };

  const handleClick = (card) => {
    if (flipped.length === 2 || flipped.includes(card.uuid) || matched.includes(card.id)) return;

    const newFlipped = [...flipped, card.uuid];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves((prev) => prev + 1);
      const [first, second] = newFlipped;
      const firstCard = cards.find((c) => c.uuid === first);
      const secondCard = cards.find((c) => c.uuid === second);

      if (firstCard.id === secondCard.id) {
        setMatched((prev) => [...prev, firstCard.id]);
        setFlipped([]);
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  const getCardSize = () => {
    if (pairCount <= 6) return 120;
    if (pairCount <= 8) return 110;
    if (pairCount <= 12) return 100;
    if (pairCount <= 15) return 90;
    if (pairCount <= 18) return 85;
    return 75;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-white">
        <div className="text-3xl font-extrabold">Loading memory game...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 text-white py-16 px-4">
      <h1 className="text-4xl md:text-5xl font-extrabold text-center mb-6 drop-shadow-lg">🧠 Nubbi Memory Challenge</h1>

      <div className="flex justify-center items-center gap-4 mb-8">
        <label htmlFor="pairs" className="text-lg font-bold">Choose Difficulty:</label>
        <select
          id="pairs"
          className="text-black font-bold rounded-lg px-4 py-2"
          value={pairCount}
          onChange={(e) => setPairCount(parseInt(e.target.value))}
        >
          <option value={6}>6 Pairs (Easy)</option>
          <option value={8}>8 Pairs</option>
          <option value={12}>12 Pairs</option>
          <option value={15}>15 Pairs</option>
          <option value={18}>18 Pairs (Hard)</option>
          <option value={24}>24 Pairs (Insane)</option>
        </select>
      </div>

      <p className="text-center mb-2 text-lg">Find all the matching pairs!</p>
      <p className="text-center mb-8 text-xl font-bold text-yellow-300">Moves: {moves}</p>

      <div
        className="grid gap-2 sm:gap-4 max-w-7xl mx-auto"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(${getCardSize()}px, 1fr))`,
        }}
      >
        {cards.map((card) => {
          const isFlipped = flipped.includes(card.uuid) || matched.includes(card.id);
          return (
            <div
              key={card.uuid}
              onClick={() => handleClick(card)}
              className="rounded-xl shadow-lg border-2 border-white cursor-pointer bg-black bg-opacity-20 hover:scale-105 transition"
              style={{
                aspectRatio: '1 / 1',
                maxWidth: `${getCardSize()}px`,
                width: '100%',
                backgroundImage: `url(${isFlipped ? card.image : '/logooficial.png'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          );
        })}
      </div>

      <div className="mt-16 text-center">
        <h2 className="text-2xl font-extrabold mb-4 drop-shadow">🏆 Top Players (Demo)</h2>
        <ul className="text-lg space-y-2">
          <li>1. Nubbisaur - 42 moves</li>
          <li>2. Glorpzilla - 47 moves</li>
          <li>3. Captain Crunchy - 51 moves</li>
          <li>4. Pixel Ninja - 58 moves</li>
          <li>5. Fluffmaster - 63 moves</li>
        </ul>
      </div>
    </div>
  );
}
